import { FeatureFlag, Role } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { localeList, P, timezoneList, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSection } from '../../components/forms/FormSection';
import { FormStack } from '../../components/forms/FormStack';
import { HookedFeatureFlagSwitches } from '../../components/forms/HookedFeatureFlagSwitches';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedFormFieldset } from '../../components/forms/HookedFormFieldset';
import { HookedImageUploader } from '../../components/forms/HookedImageUploader';
import { HookedInput } from '../../components/forms/HookedInput';
import { HookedSelect } from '../../components/forms/HookedSelect';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { useUpdateOrganizationMutation } from '../../graphql/hooks/useUpdateOrganizationMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { useRole } from '../../hooks/useRole';
import { zodResolver } from '../../utils/zod';

type FormValues = {
  name: string;
  timezone: string;
  locale: string;
  logo?: string | null;
  featureFlags: FeatureFlag[];
};

const schema: z.ZodSchema<FormValues> = z.object({
  name: z.string(),
  timezone: z.string(),
  locale: z.string(),
  logo: z.string().nullish(),
  featureFlags: z.array(z.enum(FeatureFlag)),
});

export const BasicSettings = () => {
  const { organization, successToast, errorToast } = useAppContext();

  const hasSupportRole = useRole(Role.Support);

  const mutation = useUpdateOrganizationMutation();

  const defaultValues = {
    name: organization.name,
    timezone: organization.timezone,
    locale: organization.locale,
    logo: organization.logo,
    featureFlags: organization.featureFlags,
  };
  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values: FormValues) => {
    const request = P.pickBy(values, (value, key) => {
      return !P.isEqual(value, defaultValues[key]);
    });

    await mutation
      .mutateAsync({
        organizationId: organization.id,
        updateOrganizationRequest: request,
      })
      .then(() => {
        successToast('Basic settings updated successfully');
      })
      .catch((err) => {
        errorToast(`Error updating basic settings: ${err.message}`);
      });
  };

  React.useEffect(() => {
    methods.reset({
      name: organization.name,
      timezone: organization.timezone,
      locale: organization.locale,
      logo: organization.logo,
      featureFlags: organization.featureFlags,
    });
  }, [
    methods,
    organization.name,
    organization.timezone,
    organization.logo,
    organization.locale,
    organization.featureFlags,
  ]);

  const timezoneOptions = timezoneList.map((timezone) => ({
    key: timezone,
    value: timezone,
    label: timezone,
    raw: timezone,
  }));

  const localeOptions = localeList.map((locale) => ({
    key: locale,
    value: locale,
    label: locale,
    raw: locale,
  }));

  return (
    <FormProvider {...methods}>
      <FormStack as='form' onSubmit={methods.handleSubmit(onSubmit)} maxW='lg'>
        <FormSection heading='Basic' description='Basic settings related to your organization'>
          <ChakraV3.Separator />
          <FormStack>
            <HookedFormField
              name='name'
              label='Organization name'
              helperText='The name of your organization'
            >
              <HookedInput name='name' size='sm' />
            </HookedFormField>

            <HookedFormField
              name='timezone'
              label='Timezone'
              helperText='The main timezone of your organization'
            >
              <HookedSelect name='timezone' options={timezoneOptions} />
            </HookedFormField>

            <HookedFormField
              name='locale'
              label='Locale'
              helperText='The date format for your organization'
            >
              <HookedSelect name='locale' options={localeOptions} />
            </HookedFormField>
            <HookedFormField label='Logo' name='logo'>
              <HookedImageUploader name='logo' maxWidth={150} maxHeight={120} />
            </HookedFormField>
            {hasSupportRole && (
              <HookedFormFieldset label='Feature flags' name='featureFlags'>
                <HookedFeatureFlagSwitches name='featureFlags' />
              </HookedFormFieldset>
            )}
          </FormStack>
        </FormSection>
        <ChakraV3.Separator />
        <ChakraV3.HStack>
          <HookedSubmitButton>Save</HookedSubmitButton>
        </ChakraV3.HStack>
      </FormStack>
    </FormProvider>
  );
};
